import React, { useEffect } from 'react';
import { useLatest } from 'ahooks';
import useChart from '@intelligence/v3/_hooks/chart/useChart';
import { LANGUAGE_EN } from '@home/_config/config';
import { getPieChartDynamicOption } from './utils';
import { PIE_CHART_STATIC_OPTION } from './constants';

/**
 * 饼状图组件
 */
const PieChart: React.FC<IPieChartProps> = (props) => {
  const {
    locale = LANGUAGE_EN,
    loading = true,
    data,
    highlightId,
    metricName,
    timeRange,
    centerTitle,
    centerTitleConfigs,
    showLabel = true,
    showLegend = true,
    tooltipFormatter,
    valueFormatter,
    labelFormatter,
    legendFormatter,
    onPieClick,
    updateDeps,
  } = props;

  // 使用 useLatest 保持最新引用
  const tooltipFormatterLatest = useLatest(tooltipFormatter);
  const valueFormatterLatest = useLatest(valueFormatter);
  const labelFormatterLatest = useLatest(labelFormatter);
  const legendFormatterLatest = useLatest(legendFormatter);

  // 初始化 chart
  const { chartNode, chartInstance, option, setOption } = useChart({
    locale: locale as string,
    initialOption: PIE_CHART_STATIC_OPTION,
  });

  /**
   * 更新图表配置
   */
  const updatePieChartOption = (chartData?: IPieChartData[] | null) => {
    if (!chartInstance.current || chartInstance.current.isDisposed()) return;
    if (!chartData || chartData.length === 0) {
      setOption({
        ...option,
        legend: { show: false },
        series: [],
      });
      return;
    }

    // 生成动态配置
    const dynamicOption = getPieChartDynamicOption({
      data: chartData,
      highlightId,
      metricName,
      timeRange,
      centerTitle,
      centerTitleConfigs,
      showLabel,
      showLegend,
      tooltipFormatter: tooltipFormatterLatest.current,
      valueFormatter: valueFormatterLatest.current,
      labelFormatter: labelFormatterLatest.current,
      legendFormatter: legendFormatterLatest.current,
    });

    // 合并配置
    const newOption = {
      ...option,
      ...dynamicOption,
      tooltip: {
        ...option?.tooltip,
        ...dynamicOption?.tooltip,
      },
      legend: {
        ...option?.legend,
        ...dynamicOption?.legend,
      },
    };

    setOption(newOption);
  };

  // 数据变化时更新图表
  useEffect(() => {
    if (loading) return;
    updatePieChartOption(data);
  }, [
    locale,
    loading,
    data,
    highlightId,
    metricName,
    timeRange,
    centerTitle,
    centerTitleConfigs,
    showLabel,
    showLegend,
    updateDeps,
  ]);

  // 使用 useLatest 保持 onPieClick 最新引用
  const onPieClickLatest = useLatest(onPieClick);

  // 监听饼状图扇形点击事件
  useEffect(() => {
    const chart = chartInstance.current;
    if (!chart || !onPieClickLatest.current) return;

    const handleClick = (params: any) => {
      const clickParams: IPieChartClickParams = {
        name: params.name,
        value: params.value,
        dataIndex: params.dataIndex,
        seriesIndex: params.seriesIndex,
        data: data?.[params.dataIndex] as IPieChartData,
      };
      onPieClickLatest.current?.(clickParams);
    };

    chart.on('click', handleClick);

    return () => {
      chart.off('click', handleClick);
    };
  }, [locale, data]);

  return (
    <div className="dbi-chart-con">
      <div className="dbi-chart-con__wrapper dbi-chart-pie">
        <div className="dbi-chart-con__inner dbi-chart-pie-main" ref={chartNode} />
      </div>
    </div>
  );
};

export default PieChart;
